<template>
  <div
    v-if="noCenters"
    class="no-items"
  >
    <Vue3Lottie
      v-if="isLoading"
      :animation-data="loaderJSON"
      :height="200"
      :width="200"
    />
    <NoCenterAvailable
      v-else
      :content="contentForNoCenters"
    />
  </div>
  <div v-else>
    <div
      v-if="!stepConfirmation"
      ref="stepperWrapper"
      class="stepper-wrapper"
    >
      <n-steps
        :current="currentStep"
        :theme-overrides="themeOverrides"
        @update:current="handleStepClick"
      >
        <n-step
          v-for="(step, index) in filteredSteps"
          :key="index"
          :ref="(el) => (stepRefs[index] = el)"
          :title="step.title"
          :theme-overrides="themeOverrides"
          :disabled="index > currentStep - 1"
        />
      </n-steps>
    </div>
    <div class="steps-wrapper">
      <div class="step">
        <!-- Step 1 Parcours SPA -->
        <div
          v-if="currentStep === 1 && journeyType === 'center'"
          class="list-container"
        >
          <SvgLoader v-if="isLoading" />
          <ResultsList
            v-else
            :items="centers"
            type="center"
            @select-item="handleSelectItem"
          />
        </div>
        <!-- Step 1 Parcours SOINS -->
        <div
          v-if="currentStep === 1 && journeyType === 'service'"
          class="list-container"
        >
          <SvgLoader v-if="isLoading" />
          <ResultsList
            v-else
            :items="services"
            :centers="centersFromSelectedServiceFamilyProducts"
            type="service"
            @select-item="handleSelectItem"
            @select-center="selectCenter"
          />
        </div>
        <!-- Step 2 Parcours SPA -->
        <div
          v-if="currentStep === 2 && journeyType === 'center'"
          class="list-container"
        >
          <ResultsList
            :items="servicesForSelectedCenter"
            type="service"
            :centers="[selectedCenter]"
            @select-item="handleSelectItem"
          />
        </div>
        <!-- Step 3 SPA et SOIN -->
        <div
          v-if="
            (currentStep === 3 && journeyType === 'center') ||
            (currentStep === 2 && journeyType === 'service')
          "
          class="list-container"
        >
          <StepForm
            :slot="selectedSlot"
            :center="selectedCenter"
            :service="selectedService"
            @select-item="handleSelectItem"
          />
        </div>
        <!-- Step Confirmation -->
        <div
          v-if="stepConfirmation"
          class="list-container"
        >
          <StepConfirmation />
        </div>

        <BookingRecap
          :center="selectedCenter || bookingStore.currentCenter"
          :service="
            currentStep >= 2
              ? selectedService
              : bookingStore.currentService || undefined
          "
          :current-step="currentStep"
          :max-step-reached="maxStepReached"
          :beneficiary="undefined"
          :has-multiple-centers="hasMultipleCenters"
          @select-item="handleSelectItem"
          @item-added-to-cart="handleAddToCart"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watchEffect, onMounted, watch, computed, nextTick } from "vue";
import { useQuery } from "villus";
import haversine from "haversine-distance";
import { graphqlStepsGetCentersAndServicesVariants } from "../utils/queryGraphql";

import { useRouter, useRoute } from "vue-router";

import { useBookingStore } from "../stores/booking";
import { useTranslationsStore } from "../stores/translations";
import { usePlaceStore } from "../stores/place";

import ResultsList from "./ResultsList.vue";
import BookingRecap from "./molecules/BookingRecap.vue";
import StepForm from "./StepForm.vue";
import StepConfirmation from "./StepConfirmation.vue";
import NoCenterAvailable from "./NoCenterAvailable.vue";
import SvgLoader from "./molecules/SvgLoader.vue";

const hasMultipleCenters = computed(() => {
  if (
    journeyType.value === "service" &&
    currentStep.value === 1 &&
    centersFromSelectedServiceFamilyProducts.value.length > 1
  ) {
    return true;
  }
  return false;
});

const props = defineProps({
  steps: {
    type: Array,
    default: () => [],
  },
  currentStepProp: {
    type: Number,
    default: 1,
  },
  centerData: {
    type: Array,
    default: () => [],
  },
  initialStep: {
    type: Number,
    default: 1,
  },
  isLoaderInit: {
    type: Boolean,
    default: false,
  },
});

// Emits
defineEmits(["updateCurrentStep", "select-item"]);

// Stores
const bookingStore = useBookingStore();
const translationsStore = useTranslationsStore();
const placeStore = usePlaceStore();
const router = useRouter();
const route = useRoute();

// Step logic
const currentStep = ref(parseInt(route.query.step, 10));
const maxStepReached = ref(parseInt(route.query.step, 10));
const filteredSteps = computed(() => props.steps.slice(0, -1));
const stepRefs = [];
const stepperWrapper = ref(null);

// Booking state
const selectedCenter = ref(null);
const selectedService = ref(null);
const selectedSlot = ref(null);
const collection = ref(null);

// Journey
const journeyType = computed(() => bookingStore.journeyType);

// Center/service data
const centers = ref([]);
const services = ref([]);
const servicesForSelectedCenter = ref([]);
const centersFromSelectedServiceFamilyProducts = ref([]);
const noCenters = ref(false);
const isLoading = ref(false);
const stepConfirmation = ref(false);

//Language
const lang = route.params.lang === "en" ? "en" : "";

const { data, execute } = useQuery({
  query: graphqlStepsGetCentersAndServicesVariants,
  variables: {
    language: translationsStore.getShopifyLocale(),
  },
});
const findCenterId = (data, targetIdCenter) => {
  for (let productEdge of data.products.edges) {
    if (productEdge.node && productEdge.node.centersList) {
      const references = productEdge.node.centersList.references.edges;
      for (let reference of references) {
        const node = reference.node;
        const fields = node.fields;

        for (let field of fields) {
          if (field.key === "id_center" && field.value === targetIdCenter) {
            return node.id;
          }
        }
      }
    }
  }
  return null;
};

const getCenterFromCenterId = (collection, centerId) => {
  const centers =
    collection.products.edges[0].node.centersList.references.edges;

  for (let i = 0; i < centers.length; i++) {
    const centerNode = centers[i].node;
    const idCenterField = centerNode.fields.find(
      (field) => field.key === "id_center"
    );
    if (idCenterField && idCenterField.value === centerId.toString()) {
      const id_center =
        centerNode.fields?.find((f) => f.key === "id_center")?.value || "";
      const latitude = parseFloat(
        (
          centerNode.fields?.find((f) => f.key === "latitude_booking")?.value ||
          "0"
        ).replace(",", ".")
      );
      const longitude = parseFloat(
        (
          centerNode.fields?.find((f) => f.key === "longitude_booking")
            ?.value || "0"
        ).replace(",", ".")
      );
      const centerInfo = {
        id: centerNode.id,
        id_center,
        title:
          centerNode.fields?.find((f) => f.key === "nom_du_spa")?.value || "",
        adresse_postale_booking:
          centerNode.fields?.find((f) => f.key === "adresse_postale_booking")
            ?.value || "",
        ville: centerNode.fields?.find((f) => f.key === "ville")?.value || "",
        horaires_d_ouverture_booking:
          centerNode.fields?.find(
            (f) => f.key === "horaires_d_ouverture_booking"
          )?.value || "",
        latitude_booking: latitude,
        longitude_booking: longitude,
        reference_center_booking:
          centerNode.fields?.find((f) => f.key === "reference_spa_booking")
            ?.reference || "",
        image:
          centerNode.fields?.find((f) => f.key === "visuel_spa_booking")
            ?.reference?.image || "",
      };
      return centerInfo;
    }
  }
  return null;
};
watch(
  () => route.query,
  () => {
    execute();
    currentStep.value = parseInt(route.query.step) || 1;
  }
);
onMounted(() => {
  const storedBookingData = JSON.parse(localStorage.getItem("booking-storage"));
  if (storedBookingData?.currentCenter?.id) {
    centerSelect(storedBookingData.currentCenter);
  }

  if (route.query.step && parseInt(route.query.step) > 0) {
    document
      .querySelector(".push-service-locator")
      ?.style?.setProperty("display", "none");
  }

  isLoading.value = true;
  setTimeout(() => {
    isLoading.value = false;
  }, 500);
});

watchEffect(() => {
  if (data.value) {
    collection.value = data.value.collectionByHandle;
    console.log("collection = ", collection.value);
    bookingStore.updateServices(collection.value);
    if (route.params.step) {
      bookingStore.currentStep = route.params.step;
    }

    if (collection.value && journeyType.value === "center") {
      extractCenters(collection.value);
    } else if (
      collection.value &&
      journeyType.value === "service" &&
      route.query.lat &&
      bookingStore.serviceFamily
    ) {
      console.log("bookingStore.serviceFamily", bookingStore.serviceFamily);
      extractProductsByServiceFamily(
        collection.value,
        bookingStore.serviceFamily
      );
    } else if (
      collection.value &&
      journeyType.value === "service" &&
      bookingStore.zenotiVariantService &&
      bookingStore.currentStep === 1
    ) {
      // Cas retour en arrière de step 2 à step 1 après provenance de PDP (variant + centerId dans l'URL ms pas lat/lng)
      console.log("bookingStore in watchEffect", bookingStore.$state);
      // bookingStore.updateServiceFamily(bookingStore.currentService.serviceFamily);

      extractProductsByServiceFamily(
        collection.value,
        bookingStore.serviceFamily
      );
    }
  }
});

watch(
  () => props.isLoaderInit,
  () => {
    if (props.isLoaderInit) {
      isLoading.value = true;
      setTimeout(() => {
        isLoading.value = false;
      }, 800);
    }
  }
);

watch(
  () => bookingStore.zenotiService?.code,
  () => {
    if (bookingStore.zenotiService?.code) {
      router.push({
        name:
          journeyType.value === "service"
            ? "BookingByServices"
            : "BookingByCenters",
        params: { lang },
        query: {
          ...route.query,
          serviceId: bookingStore?.zenotiService?.code,
        },
      });
    }
  }
);
watch(currentStep, async (newVal) => {
  await nextTick();
  const wrapper = stepperWrapper.value;
  const stepEl = stepRefs[newVal - 1]?.$el;

  if (!wrapper || !stepEl) return;

  // Check if horizontal scrolling is possible
  const isScrollable = wrapper.scrollWidth > wrapper.clientWidth;
  if (!isScrollable) return;

  const wrapperRect = wrapper.getBoundingClientRect();
  const stepRect = stepEl.getBoundingClientRect();

  const offset = stepRect.left - wrapperRect.left;
  const scrollTo =
    wrapper.scrollLeft +
    offset -
    wrapper.clientWidth / 2 +
    stepEl.offsetWidth / 2;
  console.log("scrollTo", scrollTo); // eslint-disable-line
  wrapper.scrollTo({
    left: scrollTo,
    behavior: "smooth",
  });
});

watch(
  () => data.value,
  (newData) => {
    console.log("newData", newData);
    let url = route.query;
    // Direct access from url page spa
    if (url.lat && url.lng) {
      if (
        newData &&
        url.centerId &&
        url.step === "2" &&
        bookingStore.journeyType != "service"
      ) {
        collection.value = newData.collectionByHandle;

        const centerId = findCenterId(collection.value, url.centerId);
        if (centerId) {
          extractProductsForCenter(centerId);
          const currentCenterData = getCenterFromCenterId(
            collection.value,
            url.centerId
          );
          if (currentCenterData) {
            bookingStore.updateCurrentCenter(currentCenterData);
          }
        }
      }
      if (newData && url.serviceFamily) {
        bookingStore.updateServiceFamily(url.serviceFamily);
      }
    } else {
      if (
        newData &&
        url.centerId &&
        url.step === "2" &&
        bookingStore.journeyType == "service"
      ) {
        //force step 2 in store
        bookingStore.$patch({
          currentStep: 2,
        });

        if (url.serviceFamily) {
          bookingStore.updateServiceFamily(url.serviceFamily);
        }
        console.log(bookingStore);
        // Retour de page de connexion / après venue de PDP : reconstruction du store
        if (url.variant) {
          console.log("url.variant", url.variant);
          const result = getProductFromVariantId(collection.value, url.variant);
          router.push({
            query: {
              ...route.query,
              centerId: bookingStore?.center?.id_center,
            },
          });
          bookingStore.updateCurrentCenter(result.center);
          bookingStore.updateCurrentService(result.product);

          placeStore.updatePlace(result.center.ville);
          placeStore.updateInput(result.center.ville);
        }
      } else if (
        // If no center Id available
        newData &&
        url.variant &&
        url.step === "2" &&
        bookingStore.journeyType == "service"
      ) {
        // Acces depuis la page produit avec variant et sans centerId dans l'URL
        if (url.variant) {
          console.log("url.variant", url.variant);
          const result = getProductFromVariantId(collection.value, url.variant);

          router.push({
            query: {
              ...route.query,
              centerId: bookingStore?.center?.id_center,
            },
          });
          bookingStore.updateCurrentCenter(result.center);
          bookingStore.updateCurrentService(result.product);

          placeStore.updatePlace(result.center.ville);
          placeStore.updateInput(result.center.ville);
          console.log("result.center.ville ", result.center.ville);
          console.log(placeStore.$state);
        } else {
          const locale = route.params.locale || "";
          router.push(`${locale}/pages/booking-search`);
        }
      }
    }
  },
  { immediate: true }
);

const extractCenters = (collection) => {
  const urlParams = new URLSearchParams(window.location.search);
  const refLat = parseFloat(urlParams.get("lat"));
  const refLng = parseFloat(urlParams.get("lng"));

  const centerMetaObjects = collection.products?.edges?.flatMap((product) => {
    const references = product.node?.centersList?.references?.edges || [];
    return references.map((ref) => ref.node);
  });

  const seen = new Set();

  centers.value = centerMetaObjects
    .map((center) => {
      const latitude = parseFloat(
        center.fields?.find((field) => field.key === "latitude_booking")
          ?.value ?? "0"
      );
      const longitude = parseFloat(
        center.fields?.find((field) => field.key === "longitude_booking")
          ?.value ?? "0"
      );
      return {
        id: center.id,
        id_center:
          center.fields?.find((field) => field.key === "id_center")?.value ??
          "",
        title:
          center.fields?.find((field) => field.key === "nom_du_spa")?.value ??
          "",
        adresse_postale_booking:
          center.fields?.find(
            (field) => field.key === "adresse_postale_booking"
          )?.value ?? "",
        ville:
          center.fields?.find((field) => field.key === "ville")?.value ?? "",
        horaires_d_ouverture_booking:
          center.fields?.find(
            (field) => field.key === "horaires_d_ouverture_booking"
          )?.value ?? "",
        latitude_booking: latitude,
        longitude_booking: longitude,
        reference_center_booking:
          center.fields?.find((field) => field.key === "reference_spa_booking")
            ?.reference ?? "",
        image:
          center.fields?.find((field) => field.key === "visuel_spa_booking")
            ?.reference?.image ?? "",
        lien_map:
          center.fields?.find((field) => field.key === "lien_map")?.value ?? "",
      };
    })
    .filter((center) => {
      if (seen.has(center.id)) return false;
      seen.add(center.id);
      console.log("seen", seen); // eslint-disable-line

      if (refLat && refLng) {
        // Define points as objects
        const referencePoint = { lat: refLat, lng: refLng };
        const centerPoint = {
          lat: center.latitude_booking,
          lng: center.longitude_booking,
        };

        const distance = haversine(referencePoint, centerPoint);
        const limitDistance =
          parseFloat(data.value.settings.nodes[0].radius.value) * 1000; // Convert km to meters
        return distance <= limitDistance;
      }

      return true;
    });
  if (centers.value.length === 0) {
    noCenters.value = true;
  } else {
    noCenters.value = false;
  }

  bookingStore.updateCenters(centers.value);
};
// Parcours Spa : extract products corresponding to the selected center on step 1
const extractProductsForCenter = (centerId) => {
  console.log("extract products with centerId", centerId); // eslint-disable-line
  servicesForSelectedCenter.value = [];
  const storage = JSON.parse(localStorage.getItem("booking-storage"));
  if (!collection.value && !storage?.services) return;

  const productEdges =
    collection?.value?.products.edges ?? storage.services.products.edges;

  productEdges.forEach((product) => {
    const centersListData = product.node.centersList
      ? JSON.parse(product.node.centersList.value)
      : [];
    console.log("centersListData", centersListData); // eslint-disable-line

    if (centersListData.includes(centerId)) {
      console.log("includes", centerId); // eslint-disable-line
      servicesForSelectedCenter.value.push({
        id: product.node.id,
        title: product.node.title,
        image: product.node.featuredImage
          ? {
              url: product.node.featuredImage.url,
              altText: product.node.featuredImage.altText,
            }
          : null,
        badges: product.node.badges
          ? JSON.parse(product.node.badges.value)
          : [],
        minPrice: product.node.priceRange.minVariantPrice.amount,
        currencyCode: product.node.priceRange.minVariantPrice.currencyCode,
        duration: product.node.durationServices
          ? product.node.durationServices
          : "",
        iconic: product.node.iconic
          ? JSON.parse(product.node.iconic.value)
          : false,
        serviceFamily: product.node.serviceFamily
          ? JSON.parse(product.node.serviceFamily.value)
          : "",
        variants: product.node.variants.edges.map((edge) => ({
          id: edge.node.id,
          id_pms: edge.node.metafield?.value || "",
          title: edge.node.title,
        })),
      });
    } else {
      console.log("doesn't include", centerId); // eslint-disable-line
    }
  });
};
// Parcours Soins : extract products corresponding to the chosen service family
const extractProductsByServiceFamily = (collection, selectedServiceFamily) => {
  console.log(
    "extract products by service family",
    selectedServiceFamily.value
  ); // eslint-disable-line
  const storage = JSON.parse(localStorage.getItem("booking-storage"));
  const collectionEdges =
    collection?.products.edges ?? storage.services.products.edges;
  services.value = [];
  const allCenters = new Map();

  const urlParams = new URLSearchParams(window.location.search);
  const refLat =
    parseFloat(urlParams.get("lat")) ||
    bookingStore.currentCenter?.latitude_booking;
  const refLng =
    parseFloat(urlParams.get("lng")) ||
    bookingStore.currentCenter?.longitude_booking;
  const limitDistance =
    parseFloat(data.value.settings.nodes[0].radius.value) * 1000; // Convert km to meters
  console.log("limitDistance", refLat); // eslint-disable-line
  const allValidCenters = [];
  collectionEdges.forEach((product) => {
    const subcategoryRef = product.node.servicesSubcategories?.reference;
    const subhighlightRef = product.node.servicesHighlights?.reference;
    const centersRefs = product.node.centersList?.references?.edges ?? [];

    if (!subcategoryRef || !subcategoryRef.fields) return;

    const subcategoryNameField = subcategoryRef.fields.find(
      (f) => f.key === "name"
    );
    const subhighlightNameField = subhighlightRef?.fields.find(
      (f) => f.key === "name"
    );

    if (!subcategoryNameField) return;

    const subcategoryName = subcategoryNameField.value;
    const subhighlightName = subhighlightNameField?.value;
    const familyValue =
      typeof selectedServiceFamily === "string"
        ? selectedServiceFamily
        : selectedServiceFamily.value;

    if (
      subcategoryName.trim().toLowerCase() !==
        familyValue?.trim().toLowerCase() &&
      subhighlightName?.trim().toLowerCase() !==
        familyValue?.trim().toLowerCase()
    ) {
      return;
    }

    const validCenters = [];

    centersRefs.forEach(({ node: center }) => {
      const latitude = parseFloat(
        (
          center.fields?.find((f) => f.key === "latitude_booking")?.value ?? "0"
        ).replace(",", ".")
      );
      const longitude = parseFloat(
        (
          center.fields?.find((f) => f.key === "longitude_booking")?.value ??
          "0"
        ).replace(",", ".")
      );

      if (refLat && refLng) {
        const distance = haversine(
          { lat: refLat, lng: refLng },
          { lat: latitude, lng: longitude }
        );
        //console.log("distance", distance, "limitDistance", limitDistance);
        if (distance > limitDistance) return; // skip this center if too far
      }

      const centerInfo = {
        id: center.id,
        id_center:
          center.fields?.find((f) => f.key === "id_center")?.value ?? "",
        title: center.fields?.find((f) => f.key === "nom_du_spa")?.value ?? "",
        adresse_postale_booking:
          center.fields?.find((f) => f.key === "adresse_postale_booking")
            ?.value ?? "",
        ville: center.fields?.find((f) => f.key === "ville")?.value ?? "",
        horaires_d_ouverture_booking:
          center.fields?.find((f) => f.key === "horaires_d_ouverture_booking")
            ?.value ?? "",
        latitude_booking: latitude,
        longitude_booking: longitude,
        reference_center_booking:
          center.fields?.find((f) => f.key === "reference_spa_booking")
            ?.reference ?? "",
        image:
          center.fields?.find((f) => f.key === "visuel_spa_booking")?.reference
            ?.image ?? "",
      };

      validCenters.push(centerInfo);

      //stock all valid centers in array and if not already present to process out of the loop
      if (!allValidCenters.some((center) => center.id === centerInfo.id)) {
        allValidCenters.push(centerInfo);
      }

      if (!allCenters.has(center.id)) {
        allCenters.set(center.id, centerInfo);
      }
    });

    // Only push the service if at least one center is valid for it
    console.log("validCenters", validCenters); // eslint-disable-line
    if (validCenters.length > 0) {
      // console.log(
      //   "Adding service:",
      //   product.node.title,
      //   "with centers:",
      //   validCenters.length
      // );
      services.value.push({
        id: product.node.id,
        title: product.node.title,
        image: product.node.featuredImage
          ? {
              url: product.node.featuredImage.url,
              altText: product.node.featuredImage.altText,
            }
          : null,
        badges: product.node.badges
          ? JSON.parse(product.node.badges.value)
          : [],
        minPrice: product.node.priceRange.minVariantPrice.amount,
        currencyCode: product.node.priceRange.minVariantPrice.currencyCode,
        duration: product.node.durationServices ?? "",
        iconic: product.node.iconic
          ? JSON.parse(product.node.iconic.value)
          : false,
        serviceFamily: subcategoryName || subhighlightName,
        centers: validCenters,
      });
    }
  });

  // If only one center is available in services list, select it by default
  if (allValidCenters.length === 1) {
    bookingStore.updateCurrentCenter(allValidCenters[0]);
  }

  centersFromSelectedServiceFamilyProducts.value = Array.from(
    allCenters.values()
  );
  console.log(
    "centersFromSelectedServiceFamilyProducts",
    centersFromSelectedServiceFamilyProducts.value
  ); // eslint-disable-line

  if (centersFromSelectedServiceFamilyProducts.value.length === 0) {
    noCenters.value = true;
  } else {
    noCenters.value = false;
  }
};
bookingStore.updateCenters(centersFromSelectedServiceFamilyProducts);
// Parcours Soins : select center in select field
const selectCenter = (centerId) => {
  if (centerId) {
    // Filter services to only those provided in this center
    services.value = services.value.filter((service) => {
      return service.centers.some((c) => c.id_center === centerId);
    });
  }
};
const navigate = () => {
  console.log(journeyType.value); // eslint-disable-line
  router.push({
    name:
      journeyType.value === "center" ? "BookingByCenters" : "BookingByServices",
    params: { lang },
    query: {
      ...route.query,
      step: currentStep.value,
      centerId: bookingStore?.center?.id_center,
      serviceId: bookingStore?.zenotiService?.code,
    },
  });
};
const nextStep = () => {
  if (currentStep.value < props.steps.length) {
    currentStep.value++;
    navigate();
    bookingStore.$patch({
      currentStep: currentStep.value,
    });
    // Reset localStorage booking-pdp
    console.log(currentStep.value);
    console.log(localStorage.getItem("booking-pdp"));
    if (currentStep.value == 2 && localStorage.getItem("booking-pdp")) {
      localStorage.removeItem("booking-pdp");
    }
  }
};

const validateStep = () => {
  if (currentStep.value >= maxStepReached.value) {
    maxStepReached.value++;
  }
};

const centerSelect = (item) => {
  selectedCenter.value = item;
  bookingStore.updateCurrentCenter(item);
  selectedService.value = null;
  selectedSlot.value = null;
  if (journeyType.value === "center") {
    extractProductsForCenter(item.id);
  }
};

const handleSelectItem = (type, item, center) => {
  if (type === "center") {
    centerSelect(item);
  } else if (type === "service") {
    selectedService.value = item;
    selectedCenter.value = center;
    bookingStore.updateCurrentCenter(center);
    console.log("updateCurrentService in selectItem");
    bookingStore.updateCurrentService(item);
    selectedSlot.value = null;
    console.log("selectedService.value = ", selectedService.value);
  }
  validateStep();
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: "smooth",
  });
  nextStep();
};

const handleStepClick = (step) => {
  if (step < 3 && journeyType.value === "center") {
    console.log("updateCurrentService in handleStepClick");
    bookingStore.updateCurrentService(null);
    bookingStore.updateCurrentBookingSlot(null);
    bookingStore.updateZenotiVariantService(null);
    bookingStore.updateCurrentServiceVariants([]);
    bookingStore.updateCurrentShopifyVariant(null);
    bookingStore.$patch({
      currentDuration: null,
      currentParticipant: null,
    });

    if (step === 1 && journeyType.value === "center") {
      bookingStore.updateCurrentCenter(null);
      bookingStore.$patch({
        currentDuration: null,
        currentParticipant: null,
      });
    }
  }
  if (step === 1 && journeyType.value === "service") {
    console.log("updateCurrentService in handleStepClick");
    bookingStore.updateCurrentService(null);
    bookingStore.$patch({
      currentDuration: null,
      currentParticipant: null,
    });
  }
  if (step <= maxStepReached.value) {
    currentStep.value = step;
    // Updates current step in booking store
    bookingStore.moveToStep(step);
    navigate();
  }
  bookingStore.$patch({
    currentStep: currentStep.value,
  });
};
const getProductFromVariantId = (collection, inputVariant) => {
  console.log("get product from variant id");
  const storage = JSON.parse(localStorage.getItem("booking-storage"));
  const collectionEdges =
    collection?.products.edges ?? storage.services.products.edges;
  let variant = null;
  for (const product of collectionEdges) {
    if (!product.node.variants || product.node.variants.edges.length === 0)
      continue;

    const subcategoryRef = product.node.servicesSubcategories?.reference;
    const subhighlightRef = product.node.servicesHighlights?.reference;
    const centersRefs = product.node.centersList?.references?.edges ?? [];

    if (!subcategoryRef || !subcategoryRef.fields) return;

    const subcategoryNameField = subcategoryRef.fields.find(
      (f) => f.key === "name"
    );
    const subhighlightNameField = subhighlightRef?.fields.find(
      (f) => f.key === "name"
    );

    if (!subcategoryNameField) return;

    const subcategoryName = subcategoryNameField.value;
    const subhighlightName = subhighlightNameField?.value;

    let variantFound = false;
    console.log("product = ", product);
    for (const variantEdge of product.node.variants.edges) {
      if (variantEdge.node.id.indexOf(inputVariant) !== -1) {
        variantFound = true;
        variant = variantEdge;
        break;
      }
    }
    if (!variantFound) continue;

    for (const { node: center } of centersRefs) {
      const name =
        center.fields?.find((f) => f.key === "nom_du_spa")?.value || "";
      console.log("variant title :", variant.node.title);
      if (variant.node.title.indexOf(name) != -1) {
        const latitude = parseFloat(
          (
            center.fields?.find((f) => f.key === "latitude_booking")?.value ||
            "0"
          ).replace(",", ".")
        );
        const longitude = parseFloat(
          (
            center.fields?.find((f) => f.key === "longitude_booking")?.value ||
            "0"
          ).replace(",", ".")
        );

        const centerInfo = {
          id: center.id,
          id_center:
            center.fields?.find((f) => f.key === "id_center")?.value || "",
          title:
            center.fields?.find((f) => f.key === "nom_du_spa")?.value || "",
          adresse_postale_booking:
            center.fields?.find((f) => f.key === "adresse_postale_booking")
              ?.value || "",
          ville: center.fields?.find((f) => f.key === "ville")?.value || "",
          horaires_d_ouverture_booking:
            center.fields?.find((f) => f.key === "horaires_d_ouverture_booking")
              ?.value || "",
          latitude_booking: latitude,
          longitude_booking: longitude,
          reference_center_booking:
            center.fields?.find((f) => f.key === "reference_spa_booking")
              ?.reference || "",
          image:
            center.fields?.find((f) => f.key === "visuel_spa_booking")
              ?.reference?.image || "",
        };
        console.log("centerInfo == ", centerInfo);
        return {
          product: {
            id: product.node.id,
            title: product.node.title,
            image: product.node.featuredImage
              ? {
                  url: product.node.featuredImage.url,
                  altText: product.node.featuredImage.altText,
                }
              : null,
            badges: product.node.badges
              ? JSON.parse(product.node.badges.value)
              : [],
            minPrice: product.node.priceRange.minVariantPrice.amount,
            currencyCode: product.node.priceRange.minVariantPrice.currencyCode,
            duration: product.node.durationServices || "",
            iconic: product.node.iconic
              ? JSON.parse(product.node.iconic.value)
              : false,
            serviceFamily: subcategoryName || subhighlightName,
          },
          center: centerInfo,
        };
      }
    }
  }

  return null;
};
// Theme Customization
const themeOverrides = {
  stepHeaderFontSizeSmall: "12px",
  stepHeaderFontSizeMedium: "14px",
  indicatorIndexFontSizeSmall: "10px",
  indicatorIndexFontSizeMedium: "10px",
  indicatorSizeSmall: "24px",
  indicatorSizeMedium: "24px",
  indicatorTextColorProcess: "rgb(var(--brand-black) / 1)",
  indicatorColorProcess: "rgb(var(--brand-neutral-2) / 1)",
  indicatorTextColorFinish: "rgb(var(--brand-black) / 1)",
  indicatorColorFinish: "rgb(var(--brand-neutral-2) / 1)",
  indicatorTextColorWait: "rgb(var(--brand-black) / 1)",
  indicatorColorWait: "rgb(var(--brand-neutral-2) / 1)",
  headerTextColorWait: "rgb(var(--brand-black) / 1)",
  headerTextColorFinish: "rgb(var(--brand-black) / 1)",
  indicatorBorderColorProcess: "rgb(var(--brand-neutral-2) / 1)",
  indicatorBorderColorFinish: "rgb(var(--brand-neutral-2) / 1)",
  indicatorBorderColorWait: "rgb(var(--brand-neutral-2) / 1)",
  stepHeaderFontWeight: "500",
};

const handleAddToCart = () => {
  nextTick(() => {
    stepConfirmation.value = true;
  });
};

const contentForNoCenters = computed(() => {
  return [
    {
      image:
        data.value?.settings?.nodes[0].centerLocatorImage?.reference?.image,
      url: data.value?.settings?.nodes[0].centerLocatorLink?.value,
    },
    {
      image: data.value?.settings?.nodes[0].productsImage?.reference?.image,
      url: data.value?.settings?.nodes[0].productPageLink?.value,
    },
  ];
});
</script>

<style scoped>
:deep(.n-step-indicator-slot) {
  font-weight: 600;
}

:deep(.n-step-splitor) {
  display: none;
}

:deep(.n-step--process-status) {
  border-bottom: 2px solid rgb(var(--brand-primary) / 1);
}

.n-steps {
  gap: 1.5rem;
  padding-inline: 20px;
}

.n-steps .n-step {
  padding: 0.5rem 8px 0.625rem;
  flex: 0;
  align-items: center;
}

.n-steps .n-step:first-child {
  padding-inline-start: 0;
}

:deep(.n-step-content-header) {
  margin-top: 0 !important;
  margin-inline-start: 0.5rem !important;
}

.n-step--wait-status {
  opacity: 0.5;
}
.stepper-wrapper {
  overflow: auto;
}
@media screen and (min-width: 1000px) {
  .n-steps {
    gap: 2rem;
    padding-inline: 40px;
    margin-block-start: 2rem;
    width: auto;
    max-width: 100%;
  }

  .n-steps .n-step {
    padding-block-end: 0.875rem;
  }
}
</style>
